import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import Step1Upload from './steps/Step1Upload';
import Step2Rules from './steps/Step2Rules';
import Step3Report from './steps/Step3Report';
import RulesSettings from './components/RulesSettings';
import { ToastProvider } from './components/ToastContext';
import { I18nProvider, useI18n } from './i18n';
import './App.css';
import { Settings, Languages } from 'lucide-react';

/**
 * Main App Component with Routing
 * Implements multi-step wizard for document validation system
 * Step 3 includes requestID in URL: /report/{requestId}
 */

function AppContent() {
  const navigate = useNavigate();
  const { t, lang, toggleLang } = useI18n();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [markdownFiles, setMarkdownFiles] = useState(null);
  const [datalabTime, setDatalabTime] = useState(0);
  const [reportData, setReportData] = useState(null);
  const [aiTime, setAiTime] = useState(0);
  const [datalabPromise, setDatalabPromise] = useState(null);
  const [requestData, setRequestData] = useState(null);

  // Handle Step 1 completion (Upload) - receives datalabPromise
  const handleStep1Next = (data) => {
    setDatalabPromise(data.datalabPromise);
    setCurrentStep(2);
    navigate('/');
  };

  // Handle Step 2 completion (Rules) - passes datalabPromise and requestData to Step 3
  const handleStep2Next = (data) => {
    // Store the data for Step 3
    setDatalabPromise(data.datalabPromise);
    setRequestData(data.requestData);
    setDatalabTime(data.datalabTime || 0);
    setCurrentStep(3);
    
    // Navigate to Step 3 with requestID in URL
    // We'll get the requestId from the datalabPromise result or use a placeholder
    const requestId = data.requestData?.requestId || 'processing';
    navigate(`/report/${requestId}`);
  };

  // Handle back navigation
  const handleBack = () => {
    const prevStep = currentStep - 1;
    setCurrentStep(Math.max(1, prevStep));
    navigate('/');
  };

  // Handle reset (start new validation)
  const handleReset = () => {
    setCurrentStep(1);
    setMarkdownFiles(null);
    setDatalabTime(0);
    setReportData(null);
    setAiTime(0);
    setDatalabPromise(null);
    setRequestData(null);
    navigate('/');
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <div className="container header-content">
          <div className="header-logo">
            {lang === 'ar' ? (
              /* Arabic Logo - Barqium with Arabic text on left */
              <svg xmlns="http://www.w3.org/2000/svg" id="Layer_2_ar" data-name="Layer 2" viewBox="0 0 250 83.13" className="transition-all duration-500 ease-in-out px-2 w-45">
                <defs>
                  <style>
                    {`.cls-100 { fill: #457afa; } .cls-200 { fill: #141515ff; } .cls-300 { fill: url(#linear-gradient-ar); }`}
                  </style>
                  <linearGradient id="linear-gradient-ar" x1="174" y1="348" x2="174" y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#007bff"></stop>
                    <stop offset=".9" stopColor="#9a30e7"></stop>
                    <stop offset="1" stopColor="#ff00d9"></stop>
                  </linearGradient>
                </defs>
                <g id="Layer_7" data-name="Layer 7">
                  <g>
                    <g>
                      <polygon className="cls-100" points="219.4 61.53 209.32 61.53 213.79 45.77 197.59 67.37 207.67 67.37 203.2 83.13 219.4 61.53"></polygon>
                      <path className="cls-300" d="M239.7,0h-62.42c-.97,0-1.76.79-1.76,1.76v62.42c0,.97.79,1.76,1.76,1.76h18.66l1.64-2.18h-19.87V2.18h61.57v61.57h-18.82l-1.64,2.18h20.88c.97,0,1.76-.79,1.76-1.76V1.76c0-.97-.79-1.76-1.76-1.76Z"></path>
                      <g>
                        <path className="cls-200" d="M207.71,34.6c0,5.58-4.55,9.93-11.17,9.93h-14.38V9.94h13.79c6.18,0,10.38,4.05,10.38,9.14,0,4.05-2.57,6.23-4.64,7.02,2.37.79,6.03,3.36,6.03,8.5ZM187.89,14.93v9.09h7.56c3.06,0,5.14-1.93,5.14-4.6s-2.08-4.5-5.14-4.5h-7.56ZM196.05,39.54c3.51,0,5.93-2.27,5.93-5.39s-2.42-5.34-5.93-5.34h-8.15v10.72h8.15Z"></path>
                        <path className="cls-200" d="M230.04,53.42v-11.51h-.25c-.54.89-3.26,3.21-7.07,3.21-6.18,0-11.51-5.48-11.51-13.09s5.34-13.09,11.51-13.09c4,0,6.77,2.47,7.46,3.36h.25l.99-2.77h4.05v33.9h-5.44ZM230.04,32.03c0-4.79-3.11-8.05-6.72-8.05s-6.72,3.26-6.72,8.05,3.11,8.05,6.72,8.05,6.72-3.26,6.72-8.05Z"></path>
                      </g>
                    </g>
                    <g>
                      <path className="cls-200" d="M22.65,32.8c-.87-.82-1.91-1.43-3.11-1.81-1.2-.38-2.5-.57-3.9-.57s-2.69.19-3.9.57c-1.2.38-2.24.98-3.11,1.81-.87.82-1.55,1.92-2.04,3.28-.49,1.36-.74,3.01-.74,4.94,0,.38,0,.74.02,1.07.02.33.04.66.07.97-.06.6-.26,1.09-.59,1.45-.33.36-.71.7-1.14,1-.43.3-.89.6-1.38.9-.49.3-.95.67-1.38,1.12-.43.44-.78,1-1.05,1.66-.27.67-.4,1.51-.4,2.52v11.91h5.32v-10.93c0-.73.11-1.29.34-1.67.23-.38.5-.69.83-.93.32-.24.66-.46,1.02-.67.36-.21.66-.52.92-.93.82.99,1.84,1.71,3.06,2.16,1.22.45,2.6.67,4.15.67,1.42,0,2.74-.19,3.94-.57,1.2-.38,2.24-.98,3.11-1.81.87-.82,1.54-1.88,2.02-3.18.47-1.3.71-2.88.71-4.75s-.25-3.58-.74-4.94c-.49-1.36-1.17-2.45-2.04-3.28ZM19.02,45.24c-.73.9-1.84,1.35-3.36,1.35s-2.64-.44-3.38-1.32c-.74-.88-1.11-2.33-1.11-4.35,0-1.86.37-3.28,1.11-4.26.74-.98,1.87-1.47,3.38-1.47,2.96,0,4.44,1.91,4.44,5.73,0,1.99-.36,3.43-1.09,4.33Z"></path>
                      <path className="cls-200" d="M67.82,46.25c-1.2,0-2.16-.25-2.88-.76-.71-.51-1.07-1.23-1.07-2.18v-11.56c0-.32-.16-.47-.47-.47h-4.05c-.19,0-.37.03-.54.1-.17.06-.26.19-.26.38v9.85c0,.85-.02,1.57-.05,2.16-.03.58-.12,1.06-.26,1.42-.14.36-.36.63-.67.8-.3.17-.74.26-1.31.26h-6.56v-5.35c0-1.93-.24-3.55-.72-4.88-.48-1.33-1.14-2.4-2-3.22-.86-.82-1.88-1.42-3.07-1.8s-2.5-.57-3.93-.57c-1.30,0-2.54.2-3.72.59-1.18.40-2.21,1.03-3.10,1.90-.89.87-1.60,2-2.14,3.37-.54,1.38-.81,3.03-.81,4.97,0,1.81.21,3.33.64,4.56.43,1.24,1.04,2.24,1.83,3.02.79.78,1.77,1.33,2.92,1.66,1.16.33,2.48.5,3.97.5h4.8c0,.98-.09,1.79-.29,2.42-.19.63-.46,1.12-.81,1.47-.35.35-.79.59-1.33.74-.54.14-1.17.21-1.90.21h-5.56c-.32,0-.48.16-.48.48v3.8c0,.32.16.48.48.48h6.42c2.86,0,5.04-.68,6.54-2.04,1.50-1.36,2.25-3.53,2.25-6.51v-1.05h7.89c1.51,0,2.72-.18,3.61-.53.89-.35,1.56-.99,2-1.92.53,1.02,1.26,1.69,2.18,1.99.92.30,2,.46,3.23.46h62.74c1.46,0,2.8-.17,4-.5,1.21-.33,2.24-.9,3.10-1.69.86-.79,1.52-1.82,2-3.09.48-1.27.72-2.84.72-4.71s-.25-3.58-.74-4.94c-.49-1.36-1.17-2.45-2.05-3.28-.87-.82-1.91-1.43-3.12-1.81-1.21-.38-2.51-.57-3.91-.57-1.30,0-2.55.19-3.74.57-1.19.38-2.24.98-3.15,1.81-.91.82-1.62,1.92-2.14,3.28-.52,1.36-.79,3.01-.79,4.94,0,2.15.36,3.91,1.09,5.28M44.39,46.25h-4.23c-1.52,0-2.69-.36-3.49-1.09-.81-.73-1.21-2.08-1.21-4.07,0-1.8.4-3.23,1.21-4.31.81-1.07,1.92-1.61,3.35-1.61,1.52,0,2.63.48,3.33,1.44.70.96,1.05,2.37,1.05,4.24v5.39ZM134.77,45.14c-.73.81-1.87,1.21-3.42,1.21s-2.65-.42-3.37-1.26c-.73-.84-1.09-2.31-1.09-4.40,0-1.74.38-3.10,1.14-4.06.76-.97,1.87-1.45,3.33-1.45,3.01,0,4.52,1.92,4.52,5.75,0,2-.36,3.4-1.09,4.21Z"></path>
                      <path className="cls-200" d="M168.69,31.3h-4.38c-.31,0-.47.16-.47.47v11.99c0,.95-.18,1.6-.55,1.97-.36.36-.94.54-1.73.54h-3.18c-1.2,0-2.16-.25-2.88-.76-.71-.51-1.07-1.23-1.07-2.18v-11.56c0-.32-.16-.47-.47-.47h-4.38c-.31,0-.47.16-.47.48v16.97c0,1.14-.02,2.15-.07,3.02s-.19,1.62-.43,2.23c-.24.62-.61,1.09-1.12,1.40-.51.32-1.22.48-2.14.48h-1.05c-.32,0-.48.16-.48.48v3.8c0,.32.16.48.48.48h1.76c1.58,0,2.91-.21,3.98-.64,1.07-.43,1.93-1.07,2.57-1.93.64-.86,1.11-1.93,1.39-3.21.28-1.29.42-2.78.42-4.50v-.90c.54.67,1.20,1.10,1.97,1.30.78.19,1.70.29,2.78.29h4.47c1.98,0,3.39-.48,4.24-1.45.85-.97,1.27-2.48,1.27-4.54v-13.26c0-.32-.16-.48-.47-.48Z"></path>
                      <g>
                        <path className="cls-200" d="M131.46,22.56c0-1.76,1.38-3.09,3.09-3.09s3.04,1.33,3.04,3.09-1.33,3.04-3.04,3.04-3.09-1.33-3.09-3.04Z"></path>
                        <path className="cls-200" d="M125.29,22.56c0-1.76,1.38-3.09,3.09-3.09s3.04,1.33,3.04,3.09-1.33,3.04-3.04,3.04-3.09-1.33-3.09-3.04Z"></path>
                      </g>
                      <g>
                        <path className="cls-200" d="M162.64,60.61c0-1.76,1.38-3.09,3.09-3.09s3.04,1.33,3.04,3.09-1.33,3.04-3.04,3.04-3.09-1.33-3.09-3.04Z"></path>
                        <path className="cls-200" d="M60.96,60.61c0-1.76,1.38-3.09,3.09-3.09s3.04,1.33,3.04,3.09-1.33,3.04-3.04,3.04-3.09-1.33-3.09-3.04Z"></path>
                        <path className="cls-200" d="M54.78,60.61c0-1.76,1.38-3.09,3.09-3.09s3.04,1.33,3.04,3.09-1.33,3.04-3.04,3.04-3.09-1.33-3.09-3.04Z"></path>
                      </g>
                    </g>
                  </g>
                </g>
              </svg>
            ) : (
              /* English Logo - Barqium with English text on right */
              <svg xmlns="http://www.w3.org/2000/svg" id="Layer_2_en" data-name="Layer 2" viewBox="0 0 250 83.13" className="transition-all duration-500 ease-in-out px-2 w-45">
                <defs>
                  <style>
                    {`.cls-100-en { fill: #457afa; } .cls-200-en { fill: #0a1a2f; } .cls-300-en { fill: url(#linear-gradient-en); }`}
                  </style>
                  <linearGradient id="linear-gradient-en" x1="174" y1="348" x2="174" y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#007bff"></stop>
                    <stop offset=".9" stopColor="#9a30e7"></stop>
                    <stop offset="1" stopColor="#ff00d9"></stop>
                  </linearGradient>
                </defs>
                <g id="Layer_7" data-name="Layer 7">
                  <g>
                    <g>
                      <polygon className="cls-100-en" points="43.87 61.53 33.79 61.53 38.26 45.77 22.06 67.37 32.14 67.37 27.67 83.13 43.87 61.53"></polygon>
                      <path className="cls-300-en" d="M64.17,0H1.76C.79,0,0,.79,0,1.76v62.42c0,.97.79,1.76,1.76,1.76h18.66l1.64-2.18H2.18V2.18h61.57v61.57h-18.82l-1.64,2.18h20.88c.97,0,1.76-.79,1.76-1.76V1.76c0-.97-.79-1.76-1.76-1.76Z"></path>
                      <g>
                        <path className="cls-200-en" d="M32.18,34.6c0,5.58-4.55,9.93-11.17,9.93H6.63V9.94h13.79c6.18,0,10.38,4.05,10.38,9.14,0,4.05-2.57,6.23-4.64,7.02,2.37.79,6.03,3.36,6.03,8.5ZM12.36,14.93v9.09h7.56c3.06,0,5.14-1.93,5.14-4.6s-2.08-4.5-5.14-4.5h-7.56ZM20.51,39.54c3.51,0,5.93-2.27,5.93-5.39s-2.42-5.34-5.93-5.34h-8.15v10.72h8.15Z"></path>
                        <path className="cls-200-en" d="M54.51,53.42v-11.51h-.25c-.54.89-3.26,3.21-7.07,3.21-6.18,0-11.51-5.48-11.51-13.09s5.34-13.09,11.51-13.09c4,0,6.77,2.47,7.46,3.36h.25l.99-2.77h4.05v33.9h-5.44ZM54.51,32.03c0-4.79-3.11-8.05-6.72-8.05s-6.72,3.26-6.72,8.05,3.11,8.05,6.72,8.05,6.72-3.26,6.72-8.05Z"></path>
                      </g>
                    </g>
                    <g>
                      <path className="cls-200-en" d="M99.6,39.51c0,5.58-4.55,9.93-11.17,9.93h-14.38V14.85h13.78c6.18,0,10.37,4.05,10.37,9.14,0,4.05-2.57,6.22-4.64,7.02,2.37.79,6.03,3.36,6.03,8.5ZM79.78,19.84v9.09h7.56c3.06,0,5.14-1.93,5.14-4.6s-2.07-4.5-5.14-4.5h-7.56ZM87.94,44.45c3.51,0,5.93-2.27,5.93-5.39s-2.42-5.34-5.93-5.34h-8.15v10.72h8.15Z"></path>
                      <path className="cls-200-en" d="M123.91,34.02v15.41h-4.05l-.99-2.82h-.25c-.99,1.28-3.31,3.41-7.31,3.41-4.89,0-8.4-3.36-8.4-7.46,0-4.99,4.74-8.15,11.61-8.15h4v-.84c0-3.01-1.83-4.84-4.64-4.84s-4.1,1.68-4.4,3.31h-5.43c.44-4.2,3.85-8.2,9.88-8.2s9.98,4.1,9.98,10.18ZM118.52,39.9v-1.58h-3.95c-4.25,0-6.23,1.53-6.23,3.8,0,1.93,1.38,3.31,4.1,3.31,3.71,0,6.08-2.57,6.08-5.53Z"></path>
                      <path className="cls-200-en" d="M129.99,49.44v-25h4.05l.99,2.77h.25c.69-.99,2.96-3.36,6.18-3.36.89,0,1.43.05,2.08.2v5.43c-.69-.15-1.33-.2-2.22-.2-3.46,0-5.88,2.32-5.88,6.32v13.83h-5.43Z"></path>
                      <path className="cls-200-en" d="M174.9,17.77c0-1.83,1.43-3.21,3.21-3.21s3.16,1.38,3.16,3.21-1.38,3.16-3.16,3.16-3.21-1.38-3.21-3.16Z"></path>
                      <rect className="cls-200-en" x="175.39" y="24.44" width="5.43" height="25"></rect>
                      <path className="cls-200-en" d="M186.86,39.11v-14.67h5.43v14.28c0,3.71,2.37,6.27,5.68,6.27s5.83-2.57,5.83-6.27v-14.28h5.39v25h-4.05l-.99-2.82h-.25c-.79,1.09-3.31,3.41-7.02,3.41-5.88,0-10.03-4.3-10.03-10.92Z"></path>
                      <path className="cls-200-en" d="M250,34.32v15.12h-5.43v-14.97c0-3.56-1.93-5.58-4.5-5.58s-4.59,2.12-4.59,5.58v14.97h-5.43v-14.97c0-3.56-1.98-5.58-4.5-5.58s-4.59,2.12-4.59,5.58v14.97h-5.43v-25h4.05l.99,2.77h.25c.64-.99,2.77-3.36,6.08-3.36,3.95,0,6.13,2.42,7.02,4,1.14-1.68,3.66-4,7.36-4,5.09,0,8.74,3.8,8.74,10.47Z"></path>
                      <path className="cls-200-en" d="M156.32,50.03c3.8,0,6.52-2.32,7.06-3.21h.25v7.71h5.43v-30.09h-4.05l-.99,2.77h-.25c-.69-.89-3.46-3.36-7.46-3.36-6.18,0-11.51,5.48-11.51,13.09s5.34,13.09,11.51,13.09ZM156.91,28.88c3.61,0,6.72,3.26,6.72,8.05s-3.11,8.05-6.72,8.05-6.72-3.26-6.72-8.05,3.11-8.05,6.72-8.05Z"></path>
                    </g>
                  </g>
                </g>
              </svg>
            )}
          </div>
          <nav className="header-nav">
            <h3>{t('header.title')}</h3>
          </nav>
          <div className="header-user">
            <button 
              className="btn-icon lang-toggle" 
              onClick={toggleLang} 
              title={t('header.language')}
            >
              <Languages size={20} />
              <span className="lang-label">{lang === 'ar' ? 'EN' : 'AR'}</span>
            </button>
            <button className="btn-icon" onClick={() => setShowSettings(true)} title={t('header.settings')}>
              <Settings size={20} />
            </button>
          </div>
        </div>
      </header>
      
      <div className="container">
        {/* Step Content with Routes */}
        <main className="app-content container">
          <Routes>
            {/* Home Route - Steps 1 & 2 */}
            <Route path="/" element={
              <>
                {currentStep === 1 && (
                  <Step1Upload
                    onNext={handleStep1Next}
                  />
                )}
                {currentStep === 2 && (
                  <Step2Rules
                    onNext={handleStep2Next}
                    onBack={handleBack}
                    datalabPromise={datalabPromise}
                  />
                )}
              </>
            } />
            
            {/* Report Route with RequestID */}
            <Route path="/report/:requestId" element={
              <Step3ReportWrapper
                report={reportData}
                datalabPromise={datalabPromise}
                requestData={requestData}
                datalabTime={datalabTime}
                aiTime={aiTime}
                onBack={handleBack}
                onReset={handleReset}
              />
            } />
          </Routes>
        </main>

        {/* Footer */}
        <footer className="app-footer">
          <p>{t('footer.disclaimer')}</p>
          <p>{t('footer.copyright')}</p>
        </footer>
      </div>

      {showSettings && <RulesSettings onClose={() => setShowSettings(false)} />}
    </div>
  );
}

// Wrapper component to access URL params
function Step3ReportWrapper(props) {
  const { requestId } = useParams();
  
  return (
    <Step3Report
      {...props}
      urlRequestId={requestId}
    />
  );
}

// Main App wrapper with Router, Toast Provider, and I18n Provider
function App() {
  return (
    <I18nProvider>
      <ToastProvider>
        <Router>
          <AppContent />
        </Router>
      </ToastProvider>
    </I18nProvider>
  );
}

export default App;
